import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const sectionTitleStyle = {
  fontSize: { xs: '1.8rem', md: '2.8rem' },
  fontWeight: 800,
  lineHeight: 1.15,
  color: '#f8fafc',
};

const bulletStyle = {
  color: '#e2e8f0',
  fontSize: { xs: '1rem', md: '1.2rem' },
  lineHeight: 1.4,
  fontWeight: 500,
  mb: 1.2,
};

const panelBase = {
  borderRadius: '16px',
  border: '1px solid',
  p: { xs: 2.5, md: 3.5 },
  minHeight: { xs: 'auto', md: 330 },
};

/** Match Nano Banana aspectRatio. Do not use fixed height + objectFit cover. */
const wideBannerSx = {
  width: '100%',
  height: 'auto',
  aspectRatio: '16 / 9',
  objectFit: 'cover',
  display: 'block',
};

const sheen = {
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(110deg, transparent 25%, rgba(255,255,255,0.18) 50%, transparent 75%)',
    transform: 'translateX(-130%)',
    opacity: 0,
    transition: 'transform 850ms ease, opacity 350ms ease',
    pointerEvents: 'none',
  },
  '&:hover::after': {
    transform: 'translateX(130%)',
    opacity: 0.32,
  },
};

function ResilientLogo({ alt, sources, size = 54 }) {
  const [index, setIndex] = React.useState(0);
  const current = sources[index];
  const sizeSx = typeof size === 'object' ? size : { xs: size };

  if (!current) {
    return (
      <Box
        sx={{
          width: sizeSx,
          height: sizeSx,
          flexShrink: 0,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          border: '1px solid rgba(148,163,184,0.6)',
          color: '#e2e8f0',
          fontWeight: 800,
          fontSize: 14,
        }}
      >
        {alt.slice(0, 2).toUpperCase()}
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={current}
      alt={alt}
      onError={() => setIndex((prev) => prev + 1)}
      sx={{ width: sizeSx, height: sizeSx, objectFit: 'contain', flexShrink: 0 }}
    />
  );
}

const brandLockupSx = {
  display: 'flex',
  alignItems: 'center',
  gap: { xs: 1, md: 1.25 },
  minWidth: 0,
  textDecoration: 'none',
  color: 'inherit',
  cursor: 'pointer',
};

const brandNameSx = {
  color: '#f8fafc',
  fontWeight: 700,
  fontSize: { xs: '1.15rem', sm: '1.35rem', md: '1.6rem' },
  lineHeight: 1.1,
  whiteSpace: 'nowrap',
};

const brandMarkSize = { xs: 44, sm: 56, md: 72 };

function PomsoftLink({ sx, children = 'Pomsoft.net' }) {
  return (
    <Box
      component="a"
      href="https://pomsoft.net"
      target="_blank"
      rel="noopener noreferrer"
      sx={{ color: '#67e8f9', fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: '3px', ...sx }}
    >
      {children}
    </Box>
  );
}

function CtaLink({ href, onClick, variant = 'primary', children }) {
  const primary = variant === 'primary';
  return (
    <Box
      component="a"
      href={href}
      onClick={onClick}
      sx={{
        color: primary ? '#082f49' : '#e0f2fe',
        bgcolor: primary ? '#22d3ee' : '#0ea5e9',
        fontWeight: 800,
        fontSize: '1.05rem',
        px: 2.5,
        py: 1.1,
        borderRadius: '10px',
        textDecoration: 'none',
        cursor: 'pointer',
      }}
    >
      {children}
    </Box>
  );
}

const whyItems = [
  {
    title: 'The garden at 2 a.m.',
    body: 'Deer and raccoons do not wait for you to open an app. EyeSense can see them and run the sprinklers or a speaker while you sleep.',
    accent: '#22d3ee',
  },
  {
    title: 'The gate you did not hear',
    body: 'A person or a truck at the end of the drive should not live only on a hard drive. Get the alert. Keep the clip.',
    accent: '#a78bfa',
  },
  {
    title: 'Smoke before you smell it',
    body: 'A camera on the ridge can catch smoke early, on the box in the house, even if the internet is having a bad day.',
    accent: '#38bdf8',
  },
  {
    title: 'Footage nobody ever watches',
    body: 'Most recordings are a graveyard. EyeSense watches every stream on your network and only bothers you when something matters.',
    accent: '#c084fc',
  },
];

function OpenBetaAnnouncement() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 2, md: 5 },
        py: { xs: 3, md: 5 },
        background:
          'radial-gradient(circle at 12% 10%, rgba(34,211,238,0.18), transparent 35%), radial-gradient(circle at 85% 12%, rgba(168,85,247,0.22), transparent 34%), linear-gradient(180deg, #020617 0%, #0b1530 100%)',
      }}
    >
      <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: { xs: 'center', sm: 'space-between' },
            alignItems: 'center',
            gap: { xs: 1.25, sm: 2 },
            mb: { xs: 3, md: 4 },
          }}
        >
          <Box
            component="a"
            href="https://pomsoft.net"
            target="_blank"
            rel="noopener noreferrer"
            sx={brandLockupSx}
          >
            <ResilientLogo
              alt="Pomsoft.net"
              size={brandMarkSize}
              sources={[
                '/pomsoft_dark_background.png',
                '/powered_by_pomsoft.png',
                'https://pomsoft.net/images/pomsoft_dark_background.png',
              ]}
            />
            <Typography sx={brandNameSx}>Pomsoft.net</Typography>
          </Box>

          <Box
            component="a"
            href="/"
            onClick={(event) => {
              event.preventDefault();
              navigate('/');
            }}
            sx={brandLockupSx}
          >
            <ResilientLogo
              alt="EyeSense.ai"
              size={brandMarkSize}
              sources={['/eyesense-logo-v2.png', '/eyesense-logo.svg']}
            />
            <Typography sx={brandNameSx}>EyeSense.ai</Typography>
          </Box>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography sx={sectionTitleStyle}>Your cameras already see it. Nobody is watching.</Typography>
          <Typography
            sx={{
              color: '#e2e8f0',
              fontSize: { xs: '1.15rem', md: '1.65rem' },
              mt: 1.6,
              fontWeight: 500,
              maxWidth: 920,
              mx: 'auto',
              lineHeight: 1.45,
            }}
          >
            EyeSense is the night watch{' '}
            <PomsoftLink sx={{ color: 'inherit', fontWeight: 700 }}>Pomsoft.net</PomsoftLink>
            {' '}built for the property you already have cameras on. A gateway on your network watches the garden, the gate, and the tree line — and can wake the sprinklers, the speaker, or you. The model runs on the box. The video stays home.
          </Typography>
          <Typography
            sx={{
              color: '#cbd5e1',
              fontSize: { xs: '1rem', md: '1.25rem' },
              mt: 2,
              fontWeight: 500,
              maxWidth: 820,
              mx: 'auto',
            }}
          >
            The beta is open. No invite code. Create an account, then install from USB or the installer.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.5, mt: 2.5 }}>
            <CtaLink
              href="/register"
              onClick={(event) => {
                event.preventDefault();
                navigate('/register');
              }}
            >
              Create your account
            </CtaLink>
            <CtaLink
              href="/install-gateway"
              variant="secondary"
              onClick={(event) => {
                event.preventDefault();
                navigate('/install-gateway');
              }}
            >
              Install a gateway
            </CtaLink>
          </Box>
        </Box>

        <Box
          sx={{
            height: 4,
            borderRadius: 999,
            background: 'linear-gradient(90deg, #22d3ee 0%, #a78bfa 100%)',
            mb: 3.5,
          }}
        />

        <Box
          component="img"
          src="/eyesense-open-beta-hero.png"
          alt="EyeSense open beta announcement — no invite code"
          sx={{
            ...wideBannerSx,
            borderRadius: '16px',
            border: '1px solid rgba(34,211,238,0.35)',
            boxShadow: '0 0 32px rgba(34,211,238,0.16)',
            mb: 3.5,
          }}
        />

        <Box sx={{ textAlign: 'center', mb: 2.5 }}>
          <Typography sx={{ ...sectionTitleStyle, fontSize: { xs: '1.55rem', md: '2.2rem' } }}>
            Why this belongs on your property
          </Typography>
          <Typography sx={{ color: '#cbd5e1', fontSize: { xs: '1rem', md: '1.2rem' }, mt: 1, maxWidth: 720, mx: 'auto' }}>
            You already paid for the cameras. EyeSense is the missing watcher — and the hands that can do something about it.
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2.5,
            mb: 2,
          }}
        >
          {whyItems.map((item) => (
            <Box
              key={item.title}
              sx={{
                ...panelBase,
                minHeight: { xs: 'auto', md: 220 },
                position: 'relative',
                overflow: 'hidden',
                borderColor: `${item.accent}99`,
                boxShadow: `0 0 20px ${item.accent}33`,
                background:
                  'linear-gradient(180deg, rgba(2,6,23,0.82) 0%, rgba(2,6,23,0.64) 100%)',
                ...sheen,
              }}
            >
              <Typography sx={{ color: item.accent, fontSize: { xs: '1.35rem', md: '1.6rem' }, fontWeight: 800, mb: 1.2, position: 'relative', zIndex: 1 }}>
                {item.title}
              </Typography>
              <Typography sx={{ ...bulletStyle, mb: 0, position: 'relative', zIndex: 1 }}>
                {item.body}
              </Typography>
            </Box>
          ))}
        </Box>

        <Typography
          sx={{
            color: '#94a3b8',
            textAlign: 'center',
            fontSize: { xs: '0.95rem', md: '1.1rem' },
            mb: 3.5,
            fontWeight: 500,
          }}
        >
          Detection runs on your gateway. The pictures stay on your network.
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
            gap: 3,
            mb: 3.5,
          }}
        >
          <Box
            sx={{
              ...panelBase,
              position: 'relative',
              overflow: 'hidden',
              borderColor: 'rgba(34,211,238,0.6)',
              boxShadow: '0 0 24px rgba(34,211,238,0.2)',
              background:
                'radial-gradient(circle at 15% 50%, rgba(34,211,238,0.16), transparent 45%), linear-gradient(180deg, rgba(2,6,23,0.78) 0%, rgba(2,6,23,0.62) 100%)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 14,
                bottom: 14,
                left: 0,
                width: '5px',
                borderRadius: '0 8px 8px 0',
                background: 'linear-gradient(180deg, rgba(34,211,238,0.95), rgba(34,211,238,0.25))',
                boxShadow: '0 0 20px rgba(34,211,238,0.45)',
              },
              ...sheen,
            }}
          >
            <Typography sx={{ color: '#22d3ee', fontSize: '2rem', fontWeight: 800, mb: 2, position: 'relative', zIndex: 1 }}>
              What Changed
            </Typography>
            <Typography sx={{ ...bulletStyle, position: 'relative', zIndex: 1 }}>
              - Hub registration no longer asks for an invite code.
            </Typography>
            <Typography sx={{ ...bulletStyle, position: 'relative', zIndex: 1 }}>
              - Gateway signup on the USB ISO and field units is open too.
            </Typography>
            <Typography sx={{ ...bulletStyle, position: 'relative', zIndex: 1 }}>
              - Existing accounts are unchanged. You can still log in as before.
            </Typography>
            <Typography sx={{ ...bulletStyle, position: 'relative', zIndex: 1 }}>
              - The old beta-program email list is still at /beta if you want updates.
            </Typography>
          </Box>

          <Box
            sx={{
              ...panelBase,
              position: 'relative',
              overflow: 'hidden',
              borderColor: 'rgba(196,181,253,0.65)',
              boxShadow: '0 0 24px rgba(196,181,253,0.18)',
              background:
                'radial-gradient(circle at 85% 50%, rgba(168,85,247,0.16), transparent 45%), linear-gradient(180deg, rgba(2,6,23,0.8) 0%, rgba(2,6,23,0.64) 100%)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 14,
                bottom: 14,
                right: 0,
                width: '5px',
                borderRadius: '8px 0 0 8px',
                background: 'linear-gradient(180deg, rgba(168,85,247,0.95), rgba(168,85,247,0.25))',
                boxShadow: '0 0 20px rgba(168,85,247,0.45)',
              },
              ...sheen,
            }}
          >
            <Typography sx={{ color: '#a78bfa', fontSize: '2rem', fontWeight: 800, mb: 2, position: 'relative', zIndex: 1 }}>
              What To Do Next
            </Typography>
            <Typography sx={{ ...bulletStyle, position: 'relative', zIndex: 1 }}>
              1. Create an account on eyesense.ai — no code, no waitlist.
            </Typography>
            <Typography sx={{ ...bulletStyle, position: 'relative', zIndex: 1 }}>
              2. Flash the factory USB ISO, or unzip the installer and run setup.sh.
            </Typography>
            <Typography sx={{ ...bulletStyle, position: 'relative', zIndex: 1 }}>
              3. Finish the owner wizard and claim the gateway on the hub.
            </Typography>
            <Typography sx={{ ...bulletStyle, position: 'relative', zIndex: 1 }}>
              4. Point a camera at the garden, the gate, or the tree line.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            borderRadius: '14px',
            border: '1px solid rgba(148,163,184,0.45)',
            background:
              'linear-gradient(90deg, rgba(2,6,23,0.82) 0%, rgba(30,41,59,0.72) 50%, rgba(2,6,23,0.82) 100%)',
            position: 'relative',
            overflow: 'hidden',
            py: { xs: 2.2, md: 2.8 },
            px: { xs: 2, md: 3.5 },
            textAlign: 'center',
            mb: 3.5,
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background:
                'radial-gradient(circle at 30% 50%, rgba(34,211,238,0.12), transparent 40%), radial-gradient(circle at 70% 50%, rgba(168,85,247,0.12), transparent 40%)',
            },
            ...sheen,
          }}
        >
          <Typography
            onClick={() => navigate('/factory/usb-installer')}
            sx={{ color: '#f8fafc', fontSize: { xs: '1.35rem', md: '2.1rem' }, fontWeight: 600, lineHeight: 1.35, position: 'relative', zIndex: 1, cursor: 'pointer' }}
          >
            Need a new box? Ship a USB.
          </Typography>
          <Typography
            onClick={() => navigate('/install-gateway')}
            sx={{ color: '#22d3ee', fontSize: { xs: '1.35rem', md: '2.1rem' }, fontWeight: 700, lineHeight: 1.35, position: 'relative', zIndex: 1, cursor: 'pointer' }}
          >
            Already have Linux? Unzip the installer and run setup.sh.
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 1.2,
            mb: 2,
            borderRadius: '12px',
            p: 1,
            background:
              'linear-gradient(90deg, rgba(2,6,23,0.72) 0%, rgba(30,41,59,0.6) 50%, rgba(2,6,23,0.72) 100%)',
            border: '1px solid rgba(148,163,184,0.35)',
            position: 'relative',
            overflow: 'hidden',
            ...sheen,
          }}
        >
          <Chip label="Your cameras" sx={{ bgcolor: '#22d3ee', color: '#082f49', fontSize: '1rem', fontWeight: 700, px: 1, position: 'relative', zIndex: 1 }} />
          <Chip label="Watches at the edge" sx={{ bgcolor: '#0ea5e9', color: '#e0f2fe', fontSize: '1rem', fontWeight: 700, px: 1, position: 'relative', zIndex: 1 }} />
          <Chip label="Acts on what it sees" sx={{ bgcolor: '#818cf8', color: '#eef2ff', fontSize: '1rem', fontWeight: 700, px: 1, position: 'relative', zIndex: 1 }} />
          <Chip label="Open beta" sx={{ bgcolor: '#a855f7', color: '#f5f3ff', fontSize: '1rem', fontWeight: 700, px: 1, position: 'relative', zIndex: 1 }} />
        </Box>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography sx={{ color: '#e2e8f0', fontSize: { xs: '1rem', md: '1.2rem' }, mb: 1.2 }}>
            Built by <PomsoftLink />
          </Typography>
          <Typography sx={{ color: '#67e8f9', fontSize: { xs: '0.95rem', md: '1.15rem' }, opacity: 0.95 }}>
            #EyeSense #OpenBeta #Pomsoft #EdgeAI #ComputerVision #BuildInPublic
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default OpenBetaAnnouncement;
